import { updateField } from "../../../api/fieldsApi";

export const FEATURED_MAX_OPTIONS = [3, 4, 5];

export const PROJECT_LIST_FIELD_DEFS = [
  { suffix: "slug", label: "Slug", fieldType: "text" },
  { suffix: "tag", label: "Tag", fieldType: "text" },
  { suffix: "title", label: "Title", fieldType: "text" },
  { suffix: "desc", label: "Description", fieldType: "textarea" },
  { suffix: "image", label: "Card image", fieldType: "image" },
  { suffix: "featured", label: "Featured on Work page", fieldType: "boolean" },
];

export const PROJECT_SERVICE_FIELD_DEFS = Array.from({ length: 3 }, (_, i) => ({
  suffix: `service_${i}`,
  label: `Service ${i + 1}`,
  fieldType: "text",
}));

export const PROJECT_DETAIL_FIELD_DEFS = [
  { suffix: "intro", label: "Introduction", fieldType: "textarea" },
  { suffix: "intro_image", label: "Introduction image", fieldType: "image" },
  {
    suffix: "intro_image_caption",
    label: "Introduction image caption",
    fieldType: "text",
  },
  { suffix: "quote_text", label: "Quote", fieldType: "textarea" },
  { suffix: "quote_author", label: "Quote author", fieldType: "text" },
  { suffix: "mid_body", label: "Body (after quote)", fieldType: "textarea" },
  {
    suffix: "software_tools_heading",
    label: "Software & tools heading",
    fieldType: "text",
  },
  {
    suffix: "software_tools_body",
    label: "Software & tools body",
    fieldType: "textarea",
  },
  {
    suffix: "other_resources_heading",
    label: "Other resources heading",
    fieldType: "text",
  },
  { suffix: "content_image_2", label: "Second content image", fieldType: "image" },
  {
    suffix: "content_image_2_caption",
    label: "Second image caption",
    fieldType: "text",
  },
  {
    suffix: "post_image_body",
    label: "Body (after second image)",
    fieldType: "textarea",
  },
  {
    suffix: "conclusion_heading",
    label: "Conclusion heading",
    fieldType: "text",
  },
  { suffix: "conclusion", label: "Conclusion", fieldType: "textarea" },
  { suffix: "client", label: "Client", fieldType: "text" },
  { suffix: "connect_text", label: "Connect button text", fieldType: "text" },
  { suffix: "connect_link", label: "Connect button link", fieldType: "url" },
];

export const PROJECT_RESOURCE_FIELD_DEFS = Array.from({ length: 3 }, (_, i) => ({
  suffix: `other_resource_${i}`,
  label: `Resource ${i + 1}`,
  fieldType: "text",
}));

export const PROJECT_TECH_FIELD_DEFS = Array.from({ length: 6 }, (_, i) => ({
  suffix: `tech_${i}`,
  label: `Tech stack ${i + 1}`,
  fieldType: "text",
}));

export const PROJECT_FIELD_DEFS = [
  ...PROJECT_LIST_FIELD_DEFS,
  ...PROJECT_SERVICE_FIELD_DEFS,
  ...PROJECT_DETAIL_FIELD_DEFS,
  ...PROJECT_RESOURCE_FIELD_DEFS,
  ...PROJECT_TECH_FIELD_DEFS,
];

export const EMPTY_PROJECT_FORM = PROJECT_FIELD_DEFS.reduce((acc, def) => {
  acc[def.suffix] = def.fieldType === "boolean" ? false : "";
  return acc;
}, {});

export function groupProjectFields(fields) {
  const byIndex = new Map();

  fields.forEach((field) => {
    const match = /^project_(\d+)_(.+)$/.exec(field.key || "");
    if (!match) return;
    const index = Number(match[1]);
    const suffix = match[2];
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[suffix] = field;
  });

  return [...byIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, fieldMap]) => ({ index, fields: fieldMap }));
}

export function projectGroupToForm(projectGroup) {
  const form = { ...EMPTY_PROJECT_FORM };
  PROJECT_FIELD_DEFS.forEach((def) => {
    const field = projectGroup.fields[def.suffix];
    if (!field) return;
    if (def.fieldType === "boolean") {
      form[def.suffix] =
        field.value === "true" || field.value === true;
    } else {
      form[def.suffix] = field.value ?? "";
    }
  });
  return form;
}

export function getProjectTitle(projectGroup) {
  return (
    projectGroup.fields.title?.value ||
    projectGroup.fields.slug?.value ||
    `Project ${projectGroup.index}`
  );
}

export function countFeaturedProjects(projectGroups) {
  return projectGroups.filter((group) => {
    const raw = group.fields.featured?.value;
    return raw === "true" || raw === true;
  }).length;
}

export function getFeaturedMax(fields) {
  const raw = fields.find((f) => f.key === "featured_max")?.value;
  const num = Number(raw);
  if (FEATURED_MAX_OPTIONS.includes(num)) return num;
  return 5;
}

export async function syncProjectCount(sectionId, fields, projectGroups) {
  const countField = fields.find((f) => f.key === "project_count");
  if (!countField) return;

  const nextCount = String(projectGroups.length);
  if (String(countField.value ?? "") === nextCount) return;

  await updateField(countField.id, {
    label: countField.label,
    fieldType: countField.fieldType,
    isRequired: countField.isRequired,
    isPublished: countField.isPublished,
    sortOrder: countField.sortOrder,
    value: nextCount,
  });
}

export function slugifyTitle(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function formValueToFieldString(def, value) {
  if (def.fieldType === "boolean") return value ? "true" : "false";
  return value ?? "";
}
